import React, { useEffect, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import AuthenticationApiSerivce from "../../../../api/AuthServices";
import {
  isRegistrationLoadRetryExhaustedError,
  isRequestCancelledError,
} from "../../../../api/requestRetry";
import { RegistrationLayout } from "../../../RegistrationLayout/RegistrationLayout";
import { RegistrationSetupNavbar } from "../../../RegistrationLayout/RegistrationSetupNavbar";
import {
  REGISTRATION_STEP_TITLE_PROPS,
  useRegistrationStepFocus,
} from "../../../../hook/useRegistrationStepFocus";
import { useAnnounceWhen } from "../../../../hook/useA11yAnnouncements";
import { RegistrationSelectedCheck } from "../../../RegistrationLayout/RegistrationFieldError";
import RegistrationSetupWrapper from "../../RegistrationSetup.style";
import {
  REGISTRATION_SETUP_STEP_META,
  REGISTRATION_SETUP_TOTAL_STEPS,
} from "../../../../helper/constants";
import {
  fetchRegistrationAppointmentWeek,
  extractSchedulingUrl,
  getAppointmentModalityLabel,
  getAppointmentWeekSessionInfo,
  getSlotPreviewSubtext,
  getTimeslotAccessibleName,
  getTimeslotKey,
  getTimeslotWeekOffsetFromStartDate,
  mapRdSlotResponseToAppointmentWeek,
  MAX_TIMESLOT_WEEK_OFFSET,
} from "../registrationAppointmentTimeslots";
import {
  getRegistrationData,
  updateRegistrationData,
  isCardOnFileComplete,
} from "../../registrationSetupStorage";
import { registrationGoBack } from "../../registrationNavigation";
import RegistrationLoadRetryModal from "../../RegistrationLoadRetryModal";

const APPOINTMENT_STEP = REGISTRATION_SETUP_STEP_META.APPOINTMENT_TIME;

const getAppointmentStepEyebrowLabel = (translate) => {
  const stepLabel = translate("NUTRITION_COMMON_LBL_STEP_OF", {
    current: APPOINTMENT_STEP.step,
    total: REGISTRATION_SETUP_TOTAL_STEPS,
  });
  const eyebrow = translate("NUTRITION_TIMESLOT_LBL_EYEBROW");
  const separatorIndex = eyebrow.indexOf("—");
  const suffix =
    separatorIndex === -1 ? eyebrow : eyebrow.slice(separatorIndex + 1).trim();
  return `${stepLabel} — ${suffix}`.toUpperCase();
};

const SLOT_SECTION_KEYS = {
  morning: "SLOT_SECTION_MORNING",
  afternoon: "SLOT_SECTION_AFTERNOON",
  evening: "SLOT_SECTION_EVENING",
};

const RegistrationAppointmentTime = ({ history }) => {
  const { t, i18n } = useTranslation();
  const stepTitleRef = useRegistrationStepFocus();
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [appointmentWeek, setAppointmentWeek] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isWeekNavigating, setIsWeekNavigating] = useState(false);
  const [loadRetryModal, setLoadRetryModal] = useState({
    isOpen: false,
    onRetry: null,
  });
  const loadAppointmentWeekRef = useRef(null);
  const weekNavControllerRef = useRef(null);
  const timeslotLoadingMessage = t("NUTRITION_TIMESLOT_LBL_LOADING");
  useAnnounceWhen(isLoading || isWeekNavigating, timeslotLoadingMessage);

  const registrationData = getRegistrationData();
  const selectedDietitian = registrationData.selectedDietitian;

  let backPath = "/register/account/dietitians";
  if (selectedDietitian?.noPreference) {
    backPath = "/register/account/dietitians";
  } else if (selectedDietitian?.id && selectedDietitian.id !== "no-preference") {
    backPath = `/register/account/dietitians/${selectedDietitian.id}`;
  }

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevRootHeight = root?.style.height ?? "";

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    if (root) {
      root.style.height = "100vh";
      root.style.height = "100dvh";
    }

    const data = getRegistrationData();
    if (!isCardOnFileComplete(data)) {
      history.replace("/register/account/card");
      return () => {
        html.style.overflow = prevHtmlOverflow;
        body.style.overflow = prevBodyOverflow;
        if (root) root.style.height = prevRootHeight;
      };
    }
    if (!data.selectedDietitian) {
      history.replace("/register/account/dietitians");
    }

    const savedSlot = data.selectedAppointment;
    if (savedSlot?.day && savedSlot?.time) {
      setSelectedSlot(savedSlot);
    }

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      if (root) {
        root.style.height = prevRootHeight;
      }
    };
  }, [history]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const loadAppointmentWeek = async (offset = 0, options = {}) => {
      const { force = false } = options;
      const data = getRegistrationData();
      if (!data.selectedDietitian) {
        return;
      }

      if (data.selectedDietitian.noPreference) {
        history.replace("/register/account/no-preference/appointments");
        return;
      }

      if (
        !force &&
        data.appointmentTimeslotRequest?.start_date &&
        data.rdTimeslots
      ) {
        const payload = data.appointmentTimeslotRequest;
        const cachedWeek = mapRdSlotResponseToAppointmentWeek(
          data.rdTimeslots,
          payload.start_date,
          payload.end_date,
          payload.timezone
        );
        const cachedOffset = getTimeslotWeekOffsetFromStartDate(
          payload.start_date,
          payload.timezone
        );

        if (!cancelled) {
          if (
            cachedWeek.days.length !==
            (data.appointmentWeek?.days?.length ?? 0)
          ) {
            updateRegistrationData({ appointmentWeek: cachedWeek });
          }
          setWeekOffset(cachedOffset);
          setAppointmentWeek(cachedWeek);
          setIsLoading(false);
          setIsWeekNavigating(false);
        }
        return;
      }

      if (force) {
        setIsWeekNavigating(true);
      } else {
        setIsLoading(true);
      }
      setLoadRetryModal({ isOpen: false, onRetry: null });
      try {
        const timeslotResult = await fetchRegistrationAppointmentWeek({
          rdId: data.selectedDietitian.id,
          rawProfile: data.selectedDietitian.raw,
          language: i18n.language,
          apiService: AuthenticationApiSerivce,
          signal: controller.signal,
          weekOffset: offset,
        });

        if (cancelled) {
          return;
        }

        updateRegistrationData({
          appointmentTimeslotRequest: timeslotResult.payload,
          rdTimeslots: timeslotResult.rdSlot,
          appointmentWeek: timeslotResult.appointmentWeek,
          selectedAppointment: undefined,
        });
        setWeekOffset(offset);
        setSelectedSlot(null);
        setAppointmentWeek(timeslotResult.appointmentWeek);
      } catch (err) {
        if (cancelled || isRequestCancelledError(err)) {
          return;
        }
        if (isRegistrationLoadRetryExhaustedError(err)) {
          setAppointmentWeek(null);
          setLoadRetryModal({
            isOpen: true,
            onRetry: () => loadAppointmentWeekRef.current?.(offset, { force }),
          });
          return;
        }
        const errorMessage = err?.response?.data?.message || err?.message || "";
        message.error(
          errorMessage.startsWith("NUTRITION_")
            ? t(errorMessage)
            : errorMessage || t("NUTRITION_TIMESLOT_ERR_LOAD")
        );
        if (!force) {
          history.replace(backPath);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsWeekNavigating(false);
        }
      }
    };

    loadAppointmentWeekRef.current = loadAppointmentWeek;
    loadAppointmentWeek(0);

    return () => {
      cancelled = true;
      controller.abort();
      weekNavControllerRef.current?.abort();
    };
  }, [history, i18n.language, backPath, t]);

  const handleWeekChange = async (direction) => {
    const nextOffset = weekOffset + direction;
    if (
      nextOffset < 0 ||
      nextOffset > MAX_TIMESLOT_WEEK_OFFSET ||
      isLoading ||
      isWeekNavigating
    ) {
      return;
    }

    weekNavControllerRef.current?.abort();
    const controller = new AbortController();
    weekNavControllerRef.current = controller;

    const data = getRegistrationData();
    if (!data.selectedDietitian?.id) {
      return;
    }

    setIsWeekNavigating(true);
    setLoadRetryModal({ isOpen: false, onRetry: null });
    try {
      const timeslotResult = await fetchRegistrationAppointmentWeek({
        rdId: data.selectedDietitian.id,
        rawProfile: data.selectedDietitian.raw,
        language: i18n.language,
        apiService: AuthenticationApiSerivce,
        signal: controller.signal,
        weekOffset: nextOffset,
      });

      updateRegistrationData({
        appointmentTimeslotRequest: timeslotResult.payload,
        rdTimeslots: timeslotResult.rdSlot,
        appointmentWeek: timeslotResult.appointmentWeek,
        selectedAppointment: undefined,
      });
      setWeekOffset(nextOffset);
      setSelectedSlot(null);
      setAppointmentWeek(timeslotResult.appointmentWeek);
    } catch (err) {
      if (isRequestCancelledError(err)) {
        return;
      }
      if (isRegistrationLoadRetryExhaustedError(err)) {
        setLoadRetryModal({
          isOpen: true,
          onRetry: () => handleWeekChange(direction),
        });
        return;
      }
      const errorMessage = err?.response?.data?.message || err?.message || "";
      message.error(
        errorMessage.startsWith("NUTRITION_")
          ? t(errorMessage)
          : errorMessage || t("NUTRITION_TIMESLOT_ERR_LOAD")
      );
    } finally {
      setIsWeekNavigating(false);
    }
  };

  const handleSelectSlot = (cell, rowTime) => {
    if (cell.status !== "available") {
      return;
    }

    const schedulingUrl =
      cell.schedulingUrl || extractSchedulingUrl(cell.slot);
    const slot = {
      day: cell.dayKey,
      time: rowTime,
      dateKey: cell.dateKey,
      startTime: cell.slot?.start_time ?? cell.slot?.startTime,
      schedulingUrl,
      location: cell.slot?.location,
      type: cell.slot?.type,
      raw: cell.slot ?? null,
    };

    setSelectedSlot(slot);
    updateRegistrationData({ selectedAppointment: slot });
  };

  const handleBook = () => {
    if (!selectedSlot) {
      return;
    }

    if (selectedSlot.schedulingUrl) {
      updateRegistrationData({ selectedAppointment: selectedSlot });
      history.push("/calendly-booking");
      return;
    }

    updateRegistrationData({
      selectedAppointment: selectedSlot,
      bookedAppointment: true,
    });
    history.push("/register/account/confirmation");
  };

  const previewText = selectedSlot
    ? `${selectedSlot.day} · ${selectedSlot.time}`
    : "";

  const canBookAppointment = Boolean(
    selectedSlot?.day && selectedSlot?.time && !isLoading
  );

  const slotPreviewSubtext = selectedSlot
    ? getSlotPreviewSubtext(registrationData, appointmentWeek, selectedSlot, t)
    : "";

  const modalityLabel = getAppointmentModalityLabel(
    registrationData,
    appointmentWeek
  );

  const hasAvailableSlots =
    appointmentWeek?.periods?.some((period) =>
      period.rows.some((row) =>
        row.cells.some((cell) => cell.status === "available")
      )
    ) ?? false;
  const selectedSlotKey =
    selectedSlot?.day && selectedSlot?.time
      ? getTimeslotKey(selectedSlot.day, selectedSlot.time)
      : null;

  return (
    <RegistrationSetupWrapper>
      <RegistrationLayout
        containScroll
        header={
          <RegistrationSetupNavbar
            stepMeta={APPOINTMENT_STEP}
            totalSteps={REGISTRATION_SETUP_TOTAL_STEPS}
            onBack={() => registrationGoBack(history)}
          />
        }
      >
        <div className="scroll-body">
          <div
            className="content-card content-card-appointment"
            aria-busy={isWeekNavigating}
          >
            {isWeekNavigating ? (
              <div className="appointment-week-loading-overlay">
                <div className="discovery-spinner-wrap">
                  <div className="discovery-spinner" aria-hidden />
                </div>
                <p className="section-sub">
                  {t("NUTRITION_TIMESLOT_LBL_LOADING")}
                </p>
              </div>
            ) : null}
            <div className="content-card-scroll">
              <div className="section-eyebrow appointment-step">
                {getAppointmentStepEyebrowLabel(t)}
              </div>
              <h2
                className="section-title"
                id="appointment-times-title"
                ref={stepTitleRef}
                {...REGISTRATION_STEP_TITLE_PROPS}
              >
                {t("BY_RD_BTN_BROWSE_AVAILABLE_TIMES")}
              </h2>
              <p className="section-sub">
                {t("NUTRITION_TIMESLOT_DESC_SUBTITLE")}
              </p>

              {isLoading ? (
                <p className="section-sub">{t("NUTRITION_TIMESLOT_LBL_LOADING")}</p>
              ) : appointmentWeek ? (
                <div className="appointment-week-body">
                  <div className="appointment-week-header">
                    <div
                      className="appointment-week-nav"
                      role="group"
                      aria-label={t("NUTRITION_TIMESLOT_ARIA_WEEK_NAV", {
                        defaultValue: "Week navigation",
                      })}
                    >
                      <button
                        type="button"
                        className="appointment-week-nav-btn"
                        onClick={() => handleWeekChange(-1)}
                        disabled={
                          weekOffset <= 0 || isLoading || isWeekNavigating
                        }
                        aria-label={t("NUTRITION_TIMESLOT_BTN_PREVIOUS_WEEK")}
                      >
                        <span className="appointment-week-nav-arrow" aria-hidden>
                          ‹
                        </span>
                        <span>{t("NUTRITION_TIMESLOT_BTN_PREVIOUS_WEEK")}</span>
                      </button>
                      <button
                        type="button"
                        className="appointment-week-nav-btn"
                        onClick={() => handleWeekChange(1)}
                        disabled={
                          weekOffset >= MAX_TIMESLOT_WEEK_OFFSET ||
                          isLoading ||
                          isWeekNavigating
                        }
                        aria-label={t("NUTRITION_TIMESLOT_BTN_NEXT_WEEK")}
                      >
                        <span>{t("NUTRITION_TIMESLOT_BTN_NEXT_WEEK")}</span>
                        <span className="appointment-week-nav-arrow" aria-hidden>
                          ›
                        </span>
                      </button>
                    </div>
                    <div className="appointment-week-note">
                      <span>
                        {getAppointmentWeekSessionInfo(modalityLabel, t)}
                      </span>
                    </div>
                  </div>

                  <div
                    className="appointment-week-grid"
                    style={{
                      "--appointment-day-count": appointmentWeek.days.length,
                    }}
                  >
                    <div className="week-header">
                      {appointmentWeek.days.map((day) => (
                        <div key={day.dayKey}>
                          <div className="day-label">{day.label}</div>
                          <div className="day-date">{day.date}</div>
                        </div>
                      ))}
                    </div>

                    {!hasAvailableSlots ? (
                      <p className="section-sub">
                        {t("NUTRITION_TIMESLOT_DESC_NO_TIMES_WEEK")}{" "}
                        {t("NUTRITION_TIMESLOT_DESC_TRY_ANOTHER")}
                      </p>
                    ) : (
                      appointmentWeek.periods.map((period) => (
                        <div key={period.id} className="appointment-period">
                          <div className="slot-time-label">
                            {t(SLOT_SECTION_KEYS[period.id])}
                          </div>
                          {period.rows.map((row) => (
                            <div
                              key={`${period.id}-${row.time}`}
                              className="slots-grid"
                            >
                              {row.cells.map((cell, index) => {
                                const day =
                                  appointmentWeek.days[index] || null;
                                const dayKey =
                                  cell.dayKey ?? day?.dayKey;
                                const slotKey = getTimeslotKey(
                                  dayKey,
                                  row.time
                                );
                                const isSelected = selectedSlotKey === slotKey;
                                const slotName = getTimeslotAccessibleName(
                                  day,
                                  row.time
                                );
                                if (cell.status !== "available") {
                                  return (
                                    <div
                                      key={`${row.time}-${index}`}
                                      className="slot slot-booked"
                                      title={t(
                                        "NUTRITION_TIMESLOT_LBL_UNAVAILABLE",
                                        { defaultValue: "Unavailable" }
                                      )}
                                    >
                                      <span aria-hidden>{row.time}</span>
                                      <span className="registration-sr-only">
                                        {t("NUTRITION_TIMESLOT_LBL_UNAVAILABLE", {
                                          defaultValue: "Unavailable",
                                        })}
                                      </span>
                                    </div>
                                  );
                                }
                                return (
                                  <button
                                    key={`${row.time}-${index}`}
                                    type="button"
                                    className={
                                      isSelected ? "slot slot-selected" : "slot"
                                    }
                                    aria-pressed={isSelected}
                                    aria-label={slotName}
                                    onClick={() => handleSelectSlot(cell, row.time)}
                                  >
                                    {isSelected ? (
                                      <span className="slot-selected-check" aria-hidden>
                                        <RegistrationSelectedCheck />
                                      </span>
                                    ) : null}
                                    {row.time}
                                  </button>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      ))
                    )}
                  </div>

                  <div
                    className={
                      previewText
                        ? "selected-slot-preview show"
                        : "selected-slot-preview"
                    }
                  >
                    <div className="slot-preview-icon" aria-hidden>
                      📅
                    </div>
                    <div>
                      <div className="slot-preview-text">{previewText}</div>
                      {slotPreviewSubtext ? (
                        <div className="slot-preview-sub">{slotPreviewSubtext}</div>
                      ) : null}
                    </div>
                  </div>

                  <div className="slot-legend">
                    <div className="slot-legend-item">
                      <span className="slot-legend-swatch slot-legend-available" />
                      {t("NUTRITION_TIMESLOT_LBL_LEGEND_AVAILABLE")}
                    </div>
                    <div className="slot-legend-item">
                      <span className="slot-legend-swatch slot-legend-booked" />
                      {t("NUTRITION_TIMESLOT_LBL_LEGEND_BOOKED")}
                    </div>
                    <div className="slot-legend-item">
                      <span className="slot-legend-swatch slot-legend-selected" aria-hidden>
                        <RegistrationSelectedCheck />
                      </span>
                      {t("NUTRITION_TIMESLOT_LBL_LEGEND_SELECTED")}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn-primary appointment-book-btn"
                    disabled={!canBookAppointment || isWeekNavigating}
                    onClick={handleBook}
                  >
                    {t("NUTRITION_TIMESLOT_BTN_BOOK_APPOINTMENT")}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </RegistrationLayout>

      <RegistrationLoadRetryModal
        isOpen={loadRetryModal.isOpen}
        onRetry={() => {
          const retry = loadRetryModal.onRetry;
          setLoadRetryModal({ isOpen: false, onRetry: null });
          retry?.();
        }}
        onCancel={() => {
          setLoadRetryModal({ isOpen: false, onRetry: null });
          history.replace(backPath);
        }}
        isRetrying={isLoading}
      />
    </RegistrationSetupWrapper>
  );
};

export default withRouter(RegistrationAppointmentTime);
